// src/services/logger.js - Smart logging control panel
/*
==============================================================================
🎛️ LOGGING CONTROL PANEL - CHANGE SETTINGS BELOW
==============================================================================

🔧 MAIN CONTROL - Change this line to control ALL logs across your app:

CURRENT_LOG_LEVEL = LOG_LEVELS.ERROR;  // 🔴 Only show critical errors
CURRENT_LOG_LEVEL = LOG_LEVELS.WARN;   // 🟡 Show warnings + errors  
CURRENT_LOG_LEVEL = LOG_LEVELS.INFO;   // 🟢 Show normal operation (RECOMMENDED)
CURRENT_LOG_LEVEL = LOG_LEVELS.DEBUG;  // 🔵 Show everything (noisy!)

💡 QUICK PRESETS:

For PRODUCTION (clean console):
const CURRENT_LOG_LEVEL = LOG_LEVELS.WARN;

For DEVELOPMENT (normal usage):  
const CURRENT_LOG_LEVEL = LOG_LEVELS.INFO;

For DEBUGGING (when fixing issues):
const CURRENT_LOG_LEVEL = LOG_LEVELS.DEBUG;

For SILENT (emergencies only):
const CURRENT_LOG_LEVEL = LOG_LEVELS.ERROR;

==============================================================================
🌐 BROWSER CONSOLE CONTROLS (Alternative method):

Type these commands in your browser console:
logger.setLevel('ERROR')  // Only errors
logger.setLevel('WARN')   // Warnings + errors
logger.setLevel('INFO')   // Normal operation  
logger.setLevel('DEBUG')  // Show everything

Or toggle debug mode:
localStorage.setItem('debugMode', 'true');   // Force show debug logs
localStorage.setItem('debugMode', 'false');  // Hide debug logs

==============================================================================
*/

// eslint-disable-next-line no-unused-vars
const isDevelopment = process.env.NODE_ENV === 'development';
const isDebugMode = localStorage.getItem('debugMode') === 'true';

// Log levels (importance ranking)
const LOG_LEVELS = {
  ERROR: 0,   // 🔴 Critical problems - ALWAYS show
  WARN: 1,    // 🟡 Potential issues - Usually show
  INFO: 2,    // 🟢 Important updates - Normal operation
  DEBUG: 3    // 🔵 Technical details - Only when debugging
};

// Auto-select level: WARN in production builds, INFO in development.
// Override by setting localStorage.setItem('logLevel', 'DEBUG') in browser.
const CURRENT_LOG_LEVEL = process.env.NODE_ENV === 'production' ? LOG_LEVELS.WARN : LOG_LEVELS.INFO;

const logger = {
  error: (...args) => {
    if (CURRENT_LOG_LEVEL >= LOG_LEVELS.ERROR) {
      console.error('🔴', ...args);
    }
  },
  
  warn: (...args) => {
    if (CURRENT_LOG_LEVEL >= LOG_LEVELS.WARN) {
      console.warn('🟡', ...args);
    }
  },
  
  info: (...args) => {
    if (CURRENT_LOG_LEVEL >= LOG_LEVELS.INFO) {
      console.log('🟢', ...args);
    }
  },
  
  debug: (...args) => {
    if (CURRENT_LOG_LEVEL >= LOG_LEVELS.DEBUG || isDebugMode) {
      console.log('🔵', ...args);
    }
  },

  // Quick helpers for common patterns
  success: (...args) => logger.info('✅', ...args),
  progress: (...args) => logger.debug('🔄', ...args),
  platform: (...args) => logger.debug('🎵', ...args)
};

// Browser console helper
logger.setLevel = (level) => {
  console.log(`🎛️ Changing log level to: ${level}`);
  
  if (level === 'ERROR') {
    console.log('🔴 Will only show: Critical errors');
  } else if (level === 'WARN') {
    console.log('🟡 Will show: Warnings + Errors');
  } else if (level === 'INFO') {
    console.log('🟢 Will show: Normal operation + Warnings + Errors');
  } else if (level === 'DEBUG') {
    console.log('🔵 Will show: Everything (noisy!)');
  }
  
  // Store in localStorage so it persists
  localStorage.setItem('logLevel', level);
  console.log('💡 Refresh the page to apply changes');
};

// Check for saved log level preference
const savedLogLevel = localStorage.getItem('logLevel');
if (savedLogLevel) {
  console.log(`🎛️ Using saved log level: ${savedLogLevel}`);
}

export default logger;